import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

// @ts-ignore
import styles from "./styles.module.scss";

import { ReactComponent as ResizeIcon } from "assets/svg/resizeIcon.svg";
import { ReactComponent as InfoSmallIcon } from "assets/svg/infoSmallGray.svg";
import { ReactComponent as AiStarIcon } from "assets/svg/aiStar.svg";
import { ReactComponent as AIAssistant } from "assets/svg/aiAssistant.svg";

import { Popover, Tooltip } from "antd";

const InputResize = ({
  name,
  value,
  onChange,
  onBlur,
  words,
  label,
  placeholder,
  error,
  errorMessage,
  handleSocket,
  tooltipMessage,
  IsAiDrawerShow,
  toolTipPopover,
  updateTutorial,
  setToolTipPopover,
}: {
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  words: number;
  label: string;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  handleSocket?: any;
  tooltipMessage?: string;
  IsAiDrawerShow?: boolean;
  toolTipPopover?: boolean;
  updateTutorial?: () => void;
  setToolTipPopover?: (value: boolean) => void;
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const maxLength = 500;

  const [wasPasted, setWasPasted] = useState(false);
  const [height, setHeight] = useState<number>(40);
  const [isWrap, setIsWrap] = useState<boolean>(false);
  const [showResizeIcon, setShowResizeIcon] = useState(false);
  const [threeDots, setThreeDots] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleMouseDown = (e: any): void => {
    const startY = e.clientY;
    const startHeight = textAreaRef.current?.offsetHeight || 84;

    const handleMouseMove = (e: MouseEvent): void => {
      const newHeight = startHeight + (e.clientY - startY);
      if (newHeight >= 40 && newHeight <= 400) {
        setHeight(newHeight);
      }
    };

    const handleMouseUp = (): void => {
      document.removeEventListener("mousemove", handleMouseMove as any);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove as any);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handlePaste = (event: React.ClipboardEvent<any>) => {
    setWasPasted(true);
    let pastedData = event.clipboardData.getData("text");

    // Remove trailing whitespace from pasted data
    pastedData = pastedData.replace(/\s+$/, "");

    // Get current cursor position and selection
    const textArea = textAreaRef.current;
    const cursorPosition = textArea?.selectionStart || 0;
    const selectionEnd = textArea?.selectionEnd || 0;

    // Calculate the new value that would result from the paste
    const beforeCursor = value.slice(0, cursorPosition);
    const afterSelection = value.slice(selectionEnd);
    const newValue = beforeCursor + pastedData + afterSelection;

    if (newValue.length > maxLength) {
      setIsError(true);
      const remainingSpace =
        maxLength - beforeCursor.length - afterSelection.length;
      const truncatedPastedData = pastedData.slice(
        0,
        Math.max(0, remainingSpace)
      );
      const finalValue = beforeCursor + truncatedPastedData + afterSelection;
      onChange(finalValue);

      setTimeout(() => {
        if (textArea) {
          const newCursorPosition = cursorPosition + truncatedPastedData.length;
          textArea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
    } else {
      setIsError(false);
      onChange(newValue);

      setTimeout(() => {
        if (textArea) {
          const newCursorPosition = cursorPosition + pastedData.length;
          textArea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
    }

    event.preventDefault();
  };

  const handleChange = (event: React.ChangeEvent<any>) => {
    setWasPasted(false);

    const value = event.target.value;

    if (value.length > 500) {
      setIsError(true);
    } else {
      setIsError(false);
    }

    onChange(value);

    setTimeout(() => {
      checkContentOverflow();
    }, 0);
  };

  const checkContentOverflow = useCallback(() => {
    const textArea = textAreaRef.current;

    if (textArea) {
      const measureElement = document.createElement("span");
      measureElement.style.position = "absolute";
      measureElement.style.visibility = "hidden";
      measureElement.style.height = "auto";
      measureElement.style.width = "auto";
      measureElement.style.whiteSpace = "nowrap";
      measureElement.style.fontSize =
        window.getComputedStyle(textArea).fontSize;
      measureElement.style.fontFamily =
        window.getComputedStyle(textArea).fontFamily;
      measureElement.style.fontWeight =
        window.getComputedStyle(textArea).fontWeight;
      measureElement.style.letterSpacing =
        window.getComputedStyle(textArea).letterSpacing;
      measureElement.style.padding = "0";
      measureElement.style.margin = "0";
      measureElement.style.border = "none";
      measureElement.textContent = value || "";

      document.body.appendChild(measureElement);
      const textWidth = measureElement.offsetWidth;
      document.body.removeChild(measureElement);

      const computedStyle = window.getComputedStyle(textArea);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const availableWidth = textArea.clientWidth - paddingLeft - paddingRight;

      const isOverflowing = textWidth > availableWidth - 2;

      if (isOverflowing) {
        setThreeDots(true);
      } else {
        setThreeDots(false);
      }

      if (isOverflowing || height > 40) {
        setShowResizeIcon(true);
      } else {
        setShowResizeIcon(false);
      }

      if (height > 43) {
        setIsWrap(true);
      } else {
        setIsWrap(false);
      }
    }
  }, [height, value]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkContentOverflow();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [value, height, checkContentOverflow]);

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        checkContentOverflow();
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    const observer = new ResizeObserver(() => {
      handleResize();
    });

    if (textAreaRef.current) {
      observer.observe(textAreaRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [checkContentOverflow]);

  return (
    <div className={styles.inputGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.inputWrapper}>
        <div
          className={styles.inputContainer}
          style={{
            background: isError ? "#FFF0F0" : "#fff",
          }}
          onClick={(e) => {
            e.stopPropagation();
            textAreaRef.current?.focus();
          }}
        >
          <textarea
            ref={textAreaRef}
            className={styles.input}
            style={{
              background: isError ? "#FFF0F0" : "",
              height: `${height}px`,
              whiteSpace: isWrap ? "pre-wrap" : "nowrap",
            }}
            value={value || ""}
            name={name}
            onBlur={(event) => onBlur && onBlur(event.target.value)}
            placeholder={placeholder}
            onChange={handleChange}
            onPaste={handlePaste}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
          />
          {!isWrap && threeDots && <div className={styles.threedots}>...</div>}
          {tooltipMessage && (
            <div
              className={styles.ai_Icon}
              style={{
                // @ts-ignore
                opacity: toolTipPopover && 1,
                // @ts-ignore
                visibility: toolTipPopover && "visible",
              }}
            >
              {toolTipPopover && updateTutorial ? (
                <Popover
                  content={
                    <>
                      <div className={styles.ai_popover}>
                        <div className={styles.ai_popoverHeader}>
                          <AIAssistant />
                        </div>
                        <div className={styles.ai_popoverContent}>
                          <h3>Suggest a purpose</h3>
                          <h6>Tap the icon to generate purpose suggestions.</h6>

                          <div className={styles.ai_popoverAction}>
                            <button onClick={updateTutorial}>Got It</button>
                          </div>
                        </div>
                      </div>
                    </>
                  }
                  open={toolTipPopover}
                  overlayClassName="ai_popover"
                  placement="right"
                  showArrow={true}
                  onOpenChange={(visible) => {
                    if (!visible) {
                      updateTutorial();
                    }
                    setToolTipPopover && setToolTipPopover(visible);
                  }}
                  // @ts-ignore
                  getPopupContainer={(trigger) => trigger.parentElement}
                >
                  <Tooltip
                    title={
                      !name
                        ? "Please enter project name first"
                        : "Suggest purposes"
                    }
                    overlayClassName="ai_tooltip"
                  >
                    <AiStarIcon
                      onClick={() => name && handleSocket("purpose")}
                    />
                  </Tooltip>
                </Popover>
              ) : (
                <Tooltip title={tooltipMessage} overlayClassName="ai_tooltip">
                  <AiStarIcon
                    onClick={() =>
                      IsAiDrawerShow &&
                      handleSocket(name == "idea" ? "brainstorm" : name)
                    }
                  />
                </Tooltip>
              )}
            </div>
          )}

          {showResizeIcon && (
            <div className={styles.resizeIcon} onMouseDown={handleMouseDown}>
              <ResizeIcon />
            </div>
          )}
        </div>

        {error && <label className={styles.error}>{errorMessage}</label>}

        <CharacterCounter
          currentCount={words}
          wasPasted={wasPasted}
          isError={isError}
        />
      </div>
    </div>
  );
};

export default InputResize;

const CharacterCounter = ({
  currentCount,
  wasPasted,
  isError,
}: {
  currentCount: number;
  wasPasted: boolean;
  isError?: boolean;
}) => {
  const maxLength = 500;

  return (
    <div className={styles.wordCount}>
      {isError ? (
        <>
          <InfoSmallIcon />
          {wasPasted ? (
            <>The content was truncated at the character limit.</>
          ) : (
            <>Exceeds the maximum character limit</>
          )}
        </>
      ) : (
        `${maxLength - currentCount} characters left`
      )}
    </div>
  );
};
